import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import SovereigntyMarkers from '../components/SovereigntyMarkers';
import GoogleMapView from '../components/GoogleMapView';
import { useHousingData } from '../hooks/useHousingData';
import { PROPERTY_TYPES } from '../constants';
import { getDistance, formatDistance } from '../utils/distance';
import { geocodeLocation, geocodePropertiesParallel, syncGeocodeCacheFromD1, syncGeocodeCacheToD1, clearGeocodeCache, clearServerGeocodeCache } from '../services/mapService';
import { geocodeGoogle } from '../services/googleMapsService';
import config from '../config';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom searched location marker (purple teardrop, matching university)
const createSearchIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position: relative; width: 40px; height: 40px;">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 2C11.72 2 5 8.72 5 17C5 27.75 20 38 20 38C20 38 35 27.75 35 17C35 8.72 28.28 2 20 2Z"
          fill="#8B5CF6" stroke="white" stroke-width="2"/>
        <g transform="translate(20, 17) translate(-8, -8)">
          <rect x="3" y="8" width="10" height="7" rx="1" fill="white"/>
          <path d="M8 3L13 7H3L8 3Z" fill="white"/>
          <rect x="6" y="10" width="1.5" height="2.5" rx="0.5" fill="#8B5CF6"/>
          <rect x="9" y="10" width="1.5" height="2.5" rx="0.5" fill="#8B5CF6"/>
        </g>
      </svg>
    </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 38],
    popupAnchor: [0, -38],
  });
};

// Custom property marker (smaller green teardrop)
const createPropertyIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position: relative; width: 28px; height: 28px;">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2C8.48 2 4 6.48 4 12C4 19.25 14 26 14 26C14 26 24 19.25 24 12C24 6.48 19.52 2 14 2Z"
          fill="#10B981" stroke="white" stroke-width="1.5"/>
        <circle cx="14" cy="12" r="4.5" fill="white"/>
      </svg>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 26],
    popupAnchor: [0, -26],
  });
};

/**
 * Build a fallback geocoding query from displayAddress -> district+city.
 * NOTE: additionalAddresses are handled FIRST by geocodePropertiesParallel.
 * This function is only for fallback and for filtering properties that need geocoding.
 */
function buildAddressQuery(property) {
  const address = property.displayAddressVi || property.displayAddress || property.addressVi || property.address || '';
  const district = property.districtVi || property.district || '';
  const city = property.cityVi || property.city || '';
  if (address && district) return `${address}, ${district}, ${city || 'Ho Chi Minh City'}`;
  if (address) return `${address}, ${city || 'Ho Chi Minh City'}`;
  if (district) return `${district}, ${city || 'Ho Chi Minh City'}`;
  return null;
}

const AddressSearchPage = () => {
  const { t, i18n } = useTranslation();
  const isVietnamese = i18n.language === 'vi';
  const { properties } = useHousingData();

  const [addressInput, setAddressInput] = useState('');
  const [searchedCoords, setSearchedCoords] = useState(null);
  const [searching, setSearching] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState({ total: 0, done: 0 });
  const [enrichedProperties, setEnrichedProperties] = useState([]);
  const [sortBy, setSortBy] = useState('distance');
  const [typeFilter, setTypeFilter] = useState('all');
  const [shortTermLeaseFilter, setShortTermLeaseFilter] = useState('all');
  const [hasSearched, setHasSearched] = useState(false);
  const [searchRadius, setSearchRadius] = useState(5);
  const [mapProvider, setMapProvider] = useState('leaflet'); // 'leaflet' | 'google'
  const geocodingRef = useRef(false);

  const handleSearch = useCallback(async () => {
    const q = addressInput.trim();
    if (!q) return;

    setSearching(true);
    setGeocodeFailed(false);
    setSearchedCoords(null);
    setEnrichedProperties([]);
    setHasSearched(true);

    try {
      // Try OSM first, fallback to Google if available
      let coords = await geocodeLocation(q, '');
      if (!coords && config.googleMaps?.apiKey) {
        coords = await geocodeGoogle(q);
      }
      if (coords) {
        setSearchedCoords(coords);
      } else {
        setGeocodeFailed(true);
      }
    } catch {
      setGeocodeFailed(true);
    }
    setSearching(false);
  }, [addressInput]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Identify properties that need geocoding — those without lat/lng
  const propertiesToGeocode = useMemo(() => {
    if (!searchedCoords) return [];
    return properties.filter((p) => {
      if (p.lat && p.lng) return false;
      return !!buildAddressQuery(p);
    });
  }, [searchedCoords, properties]);

  // Batch-geocode all properties that lack coordinates in PARALLEL
  useEffect(() => {
    if (!searchedCoords) {
      setEnrichedProperties([]);
      geocodingRef.current = false;
      return;
    }

    const alreadyReady = properties
      .filter((p) => p.lat && p.lng)
      .map((p) => ({ ...p, _geocoding: false, _geocodeFailed: false }));

    setEnrichedProperties(alreadyReady);
    setGeocodingProgress({ total: propertiesToGeocode.length, done: 0 });

    if (propertiesToGeocode.length === 0) {
      geocodingRef.current = false;
      return;
    }

    if (geocodingRef.current) return;
    geocodingRef.current = true;

    let cancelled = false;

    const runGeocoding = async () => {
      const enrichedList = await geocodePropertiesParallel(
        propertiesToGeocode,
        5, // concurrency
        (done, total) => {
          if (!cancelled) {
            setGeocodingProgress({ total, done });
          }
        }
      );

      if (!cancelled) {
        setEnrichedProperties((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newOnes = enrichedList.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newOnes];
        });
        geocodingRef.current = false;

        // Auto-push newly geocoded results to D1 so they persist
        // for other users/sessions. Fire-and-forget — don't block the UI.
        syncGeocodeCacheToD1()
          .then(r => {
            if (r.success && r.count > 0) {
              console.log(`[Geocode] Auto-pushed ${r.count} entries to D1 after batch geocoding`);
            }
          })
          .catch(err => console.warn('[Geocode] Auto-push to D1 failed:', err));
      }
    };

    runGeocoding();

    return () => {
      cancelled = true;
    };
  }, [searchedCoords, properties, propertiesToGeocode]);

  // Compute properties within radius with type filter and sort
  const nearbyProperties = useMemo(() => {
    if (!searchedCoords) return [];

    let filtered = enrichedProperties
      .filter((p) => p.lat && p.lng && !p._geocodeFailed)
      .map((p) => ({
        ...p,
        distance: getDistance(searchedCoords.lat, searchedCoords.lng, p.lat, p.lng),
      }))
      .filter((p) => p.distance <= searchRadius);

    if (typeFilter !== 'all') {
      filtered = filtered.filter((p) => {
        const propType = (p.type || '').toLowerCase().trim();
        return propType === typeFilter.toLowerCase();
      });
    }

    if (shortTermLeaseFilter === 'shortTerm') {
      filtered = filtered.filter((p) => p.shortTermLease === true);
    } else if (shortTermLeaseFilter === 'longTerm') {
      filtered = filtered.filter((p) => p.shortTermLease !== true);
    }

    if (sortBy === 'distance') {
      filtered.sort((a, b) => a.distance - b.distance);
    } else if (sortBy === 'price_asc') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_desc') {
      filtered.sort((a, b) => b.price - a.price);
    }

    return filtered;
  }, [searchedCoords, enrichedProperties, sortBy, typeFilter, searchRadius, shortTermLeaseFilter]);

  const geocodingInProgress = geocodingProgress.total > 0 && geocodingProgress.done < geocodingProgress.total;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
    }).format(Math.round(price));
  };

  const mapCenter = searchedCoords
    ? [searchedCoords.lat, searchedCoords.lng]
    : [10.7769, 106.6952];

  // Build Google Maps markers
  const googleMapMarkers = useMemo(() => {
    if (!searchedCoords) return [];
    const markers = [];

    // Searched location marker
    markers.push({
      id: 'search-location',
      position: { lat: searchedCoords.lat, lng: searchedCoords.lng },
      title: addressInput,
      popup: `<div style="font-size:13px;min-width:180px">
        <p style="font-weight:700;color:#1f2937;margin:0 0 4px">📍 ${addressInput}</p>
        <p style="color:#6b7280;font-size:12px;margin:0">Vị trí tìm kiếm</p>
      </div>`,
    });

    // Property markers
    nearbyProperties.forEach((p) => {
      markers.push({
        id: p.id,
        position: { lat: p.lat, lng: p.lng },
        title: isVietnamese && p.titleVi ? p.titleVi : p.title,
        popup: `<div style="font-size:13px;min-width:200px">
          <p style="font-weight:700;color:#1f2937;margin:0 0 4px">${isVietnamese && p.titleVi ? p.titleVi : p.title}</p>
          <p style="color:#059669;font-weight:600;margin:0 0 4px">${formatPrice(p.price)}</p>
          <p style="color:#6b7280;font-size:12px;margin:0">${p.districtVi || p.district}</p>
          <p style="color:#9ca3af;font-size:11px;margin:4px 0 0">${formatDistance(p.distance)} từ vị trí</p>
        </div>`,
      });
    });

    return markers;
  }, [searchedCoords, nearbyProperties, addressInput, isVietnamese]);

  const hasGoogleApiKey = !!config.googleMaps?.apiKey;

  // D1 sync state
  const [d1SyncState, setD1SyncState] = useState({ loading: false, message: '', count: 0 });

  const handleSyncFromD1 = useCallback(async () => {
    setD1SyncState({ loading: true, message: 'Đang đồng bộ...', count: 0 });
    const result = await syncGeocodeCacheFromD1();
    if (result.success) {
      setD1SyncState({
        loading: false,
        message: result.count > 0
          ? `Đã đồng bộ ${result.count} tọa độ từ D1`
          : 'Chưa có dữ liệu tọa độ trong D1',
        count: result.count,
      });
    } else {
      setD1SyncState({
        loading: false,
        message: `Lỗi: ${result.error || 'Không thể đồng bộ'}`,
        count: 0,
      });
    }
    setTimeout(() => {
      setD1SyncState({ loading: false, message: '', count: 0 });
    }, 5000);
  }, []);

  const handleClearCache = useCallback(async () => {
    const localCount = clearGeocodeCache();
    const serverResult = await clearServerGeocodeCache();

    const parts = [];
    if (localCount > 0) parts.push(`${localCount} local`);
    if (serverResult.success) {
      if (serverResult.memoryCleared > 0) parts.push(`${serverResult.memoryCleared} server memory`);
      if (serverResult.d1Cleared > 0) parts.push(`${serverResult.d1Cleared} D1`);
    }

    const msg = parts.length > 0
      ? `Đã xóa cache (${parts.join(', ')})`
      : 'Cache đã trống';

    setD1SyncState({
      loading: false,
      message: msg,
      count: 0,
    });
    setTimeout(() => {
      setD1SyncState(prev => prev.message ? { loading: false, message: '', count: 0 } : prev);
    }, 4000);
  }, []);

  const handlePushToD1 = useCallback(async () => {
    setD1SyncState({ loading: true, message: 'Đang đẩy lên D1...', count: 0 });
    const result = await syncGeocodeCacheToD1();
    if (result.success) {
      setD1SyncState({
        loading: false,
        message: result.count > 0
          ? `Đã đẩy ${result.count} tọa độ lên D1`
          : 'Không có tọa độ nào để đẩy',
        count: result.count,
      });
    } else {
      setD1SyncState({
        loading: false,
        message: `Lỗi: ${result.error || 'Không thể đẩy lên D1'}`,
        count: 0,
      });
    }
    setTimeout(() => {
      setD1SyncState({ loading: false, message: '', count: 0 });
    }, 5000);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminBreadcrumb />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('address.title')}</h1>
          <p className="text-gray-500 mt-2">{t('address.subtitle')}</p>
        </div>

        {/* Address Input */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Nhập địa chỉ
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                type="text"
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="VD: 123 Nguyễn Huệ, Quận 1 hoặc Bến Thành..."
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 font-medium focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !addressInput.trim()}
              className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl transition-all duration-200 text-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {searching ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('common.loading')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Tìm kiếm</span>
                </>
              )}
            </button>
          </div>
        </div>

        {!hasSearched ? (
          /* Empty state when no search performed */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="text-6xl mb-6">🔎</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Nhập địa chỉ để tìm phòng trọ</h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Gõ tên đường, khu vực, địa danh bất kỳ để tìm các phòng trọ gần đó trong bán kính 5km.
            </p>
          </div>
        ) : searchedCoords ? (
          <>
            {/* Info Bar */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 mb-6 text-white shadow-md">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-bold mb-1">
                    <span className="mr-2">📍</span>
                    {addressInput}
                  </h2>
                  <p className="text-purple-100 text-sm">Bán kính tìm kiếm: {searchRadius}km</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold">{nearbyProperties.length}</div>
                  <div className="text-purple-100 text-sm">phòng trọ tìm thấy</div>
                </div>
              </div>
            </div>

            {/* Radius Slider */}
            {searchedCoords && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-semibold text-gray-700">Bán kính tìm kiếm</label>
                  <span className="text-sm font-bold text-purple-600 bg-purple-50 px-3 py-1 rounded-full">{searchRadius} km</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSearchRadius((r) => Math.max(1, r - 1))}
                    disabled={searchRadius <= 1}
                    className="w-8 h-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-lg"
                  >
                    −
                  </button>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    value={searchRadius}
                    onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                  />
                  <button
                    onClick={() => setSearchRadius((r) => Math.min(50, r + 1))}
                    disabled={searchRadius >= 50}
                    className="w-8 h-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-lg"
                  >
                    +
                  </button>
                </div>
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1 km</span>
                  <span>50 km</span>
                </div>
              </div>
            )}

            {/* Geocoding Progress Banner */}
            {geocodingInProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 flex items-center space-x-3">
                <div className="w-5 h-5 border-2 border-blue-400 border-t-blue-600 rounded-full animate-spin flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800">
                    Đang lấy tọa độ nhà trọ... {geocodingProgress.done}/{geocodingProgress.total}
                  </p>
                  <div className="mt-2 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${(geocodingProgress.done / geocodingProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Map Toggle + D1 Sync + Clear Cache + Push Buttons */}
            <div className="flex justify-end mb-2 gap-2 flex-wrap">
              {/* Clear Cache Button */}
              <button
                onClick={handleClearCache}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                  bg-white border-gray-200 text-gray-700 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                title="Xóa bộ nhớ tạm tọa độ (sẽ tải lại từ server)"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Clear</span>
              </button>

              {/* Push to D1 Button */}
              <button
                onClick={handlePushToD1}
                disabled={d1SyncState.loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                  bg-white border-gray-200 text-gray-700 hover:bg-green-50 hover:border-green-200 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Đẩy tọa độ từ bộ nhớ tạm lên D1"
              >
                {d1SyncState.loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-green-400 border-t-green-600 rounded-full animate-spin" />
                    <span>Đang đẩy...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
                    </svg>
                    <span>Push D1</span>
                  </>
                )}
              </button>

              {/* D1 Sync Button */}
              <button
                onClick={handleSyncFromD1}
                disabled={d1SyncState.loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                  bg-white border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Đồng bộ tọa độ đã lưu từ D1 vào bộ nhớ tạm"
              >
                {d1SyncState.loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-blue-600 rounded-full animate-spin" />
                    <span>Đang đồng bộ...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Sync D1</span>
                  </>
                )}
              </button>

              {d1SyncState.message && (
                <span className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium ${
                  d1SyncState.count > 0
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : d1SyncState.message.includes('Lỗi')
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                }`}>
                  {d1SyncState.message}
                </span>
              )}

              {/* Map toggle — only when Google API key is available */}
              {searchedCoords && hasGoogleApiKey && (
                <button
                  onClick={() => setMapProvider((prev) => (prev === 'leaflet' ? 'google' : 'leaflet'))}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-sm
                    bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  {mapProvider === 'leaflet' ? (
                    <>
                      <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <span>{t('map.switchToGoogle')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      <span>{t('map.switchToOSM')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Map */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <div className="h-[450px]">
                {mapProvider === 'leaflet' ? (
                  /* OpenStreetMap (Leaflet) */
                  <MapContainer
                    key={`addr-leaflet-${addressInput}-${searchRadius}`}
                    center={mapCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {/* Hoàng Sa, Trường Sa sovereignty markers */}
                    <SovereigntyMarkers />

                    {/* Radius circle */}
                    <Circle
                      center={[searchedCoords.lat, searchedCoords.lng]}
                      radius={searchRadius * 1000}
                      pathOptions={{
                        color: '#8B5CF6',
                        fillColor: '#8B5CF6',
                        fillOpacity: 0.08,
                        weight: 2,
                        dashArray: '8, 8',
                      }}
                    />

                    {/* Searched Location Marker */}
                    <Marker
                      position={[searchedCoords.lat, searchedCoords.lng]}
                      icon={createSearchIcon()}
                    >
                      <Popup>
                        <div className="text-sm min-w-[180px]">
                          <p className="font-bold text-gray-900">
                            <span className="mr-1">📍</span>
                            {addressInput}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">Vị trí tìm kiếm</p>
                        </div>
                      </Popup>
                    </Marker>

                    {/* Property Markers */}
                    {nearbyProperties.map((p) => (
                      <Marker
                        key={p.id}
                        position={[p.lat, p.lng]}
                        icon={createPropertyIcon()}
                      >
                        <Popup>
                          <div className="text-sm min-w-[200px]">
                            <p className="font-bold text-gray-900">
                              {isVietnamese && p.titleVi ? p.titleVi : p.title}
                            </p>
                            <p className="text-primary-600 font-semibold mt-1">{formatPrice(p.price)}</p>
                            <p className="text-gray-500 text-xs mt-1 flex items-center space-x-1">
                              <svg className="w-3 h-3 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <span>{p.districtVi || p.district}</span>
                            </p>
                            <p className="text-gray-400 text-xs mt-1">{formatDistance(p.distance)} từ vị trí</p>
                            <a
                              href={`/housing/${p.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-block text-xs font-medium text-primary-600 hover:text-primary-700"
                            >
                              Xem chi tiết →
                            </a>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                ) : (
                  /* Google Maps */
                  <GoogleMapView
                    center={{ lat: searchedCoords.lat, lng: searchedCoords.lng }}
                    zoom={13}
                    markers={googleMapMarkers}
                    circle={{
                      center: { lat: searchedCoords.lat, lng: searchedCoords.lng },
                      radius: searchRadius * 1000,
                    }}
                    height="450px"
                  />
                )}
              </div>
              {/* Legend */}
              <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-100 bg-gray-50 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 rounded-full bg-purple-500 border-2 border-white shadow-sm" />
                  <span className="text-gray-600">Vị trí tìm kiếm</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
                  <span className="text-gray-600">Phòng trọ</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-0.5 border-t-2 border-dashed border-purple-400" />
                  <span className="text-gray-600">Bán kính {searchRadius}km</span>
                </div>
              </div>
            </div>

            {/* Sort & Filter Controls */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Type Filter */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('university.filterType')}</label>
                  <div className="relative">
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      <option value="all">{t('university.allTypes')}</option>
                      {PROPERTY_TYPES.map((pt) => (
                        <option key={pt.value} value={pt.value}>{t(pt.labelKey)}</option>
                      ))}
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Short Term Lease Filter */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.shortTermLease')}</label>
                  <div className="relative">
                    <select
                      value={shortTermLeaseFilter}
                      onChange={(e) => setShortTermLeaseFilter(e.target.value)}
                      className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      <option value="all">Tất cả</option>
                      <option value="shortTerm">Cho thuê ngắn ngày</option>
                      <option value="longTerm">Cho thuê dài hạn</option>
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Sort */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('university.sortBy')}</label>
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      <option value="distance">{t('university.sortDistance')}</option>
                      <option value="price_asc">{t('university.sortPriceAsc')}</option>
                      <option value="price_desc">{t('university.sortPriceDesc')}</option>
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Property List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  Phòng trọ gần đây
                  <span className="text-gray-400 font-normal ml-2">({nearbyProperties.length})</span>
                </h3>
              </div>

              {nearbyProperties.length === 0 && !geocodingInProgress ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <div className="text-4xl mb-4">🏠</div>
                  <p className="text-gray-500 font-medium">Không tìm thấy phòng trọ nào trong bán kính {searchRadius}km</p>
                  <p className="text-gray-400 text-sm mt-1">Hãy thử nhập địa chỉ khác.</p>
                </div>
              ) : nearbyProperties.length === 0 && geocodingInProgress ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                  <div className="w-10 h-10 border-3 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-500">{t('common.loading')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {nearbyProperties.map((p) => (
                    <div
                      key={p.id}
                      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow group"
                    >
                      <a
                        href={`/housing/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {/* Image */}
                        <div className="relative h-40 overflow-hidden">
                          <img
                            src={p.images[0]}
                            alt={isVietnamese && p.titleVi ? p.titleVi : p.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          {/* Distance Badge */}
                          <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm flex items-center space-x-1">
                            <svg className="w-3 h-3 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            </svg>
                            <span>{formatDistance(p.distance)}</span>
                          </div>
                          {/* Price Badge */}
                          <div className="absolute bottom-3 left-3 bg-primary-500 text-white px-2.5 py-1 rounded-lg text-xs font-semibold shadow-lg">
                            {formatPrice(p.price)}
                          </div>
                        </div>
                      </a>

                      {/* Content */}
                      <div className="p-4">
                        <a
                          href={`/housing/${p.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <h4 className="font-semibold text-gray-900 text-sm mb-1.5 line-clamp-1 group-hover:text-primary-600 transition-colors">
                            {isVietnamese && p.titleVi ? p.titleVi : p.title}
                          </h4>
                        </a>
                        <p className="text-gray-500 text-xs mb-2 line-clamp-1">
                          {p.districtVi || p.district}, {p.cityVi || p.city}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1 text-gray-400 text-xs">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{p.amenities.length} {t('detail.amenities')}</span>
                          </div>
                          <a
                            href={`/housing/${p.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            {t('university.viewDetail')} →
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Geocode failed */
          geocodeFailed && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
              <div className="text-6xl mb-6">😕</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Không tìm thấy địa chỉ</h2>
              <p className="text-gray-500 max-w-md mx-auto">
                Không thể xác định vị trí từ địa chỉ "{addressInput}". Vui lòng thử địa chỉ khác cụ thể hơn.
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AddressSearchPage;